import React, { useState, useEffect } from 'react';
import { apiCall } from '../AuthContext';
import { Video, Play, Loader2, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LearningVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiCall('/api/videos', { method: 'GET' })
      .then(r => {
        const all = r.data || [];
        setVideos(all.filter(v => v.is_public));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getEmbedUrl = (url) => {
    if (!url) return null;
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return null;
  };

  const isLocalVideo = (video) => video.video_type === 'upload' || (video.video_url && video.video_url.startsWith('/api/uploads/'));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-[#0052FF]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="learning-videos-page">
      <div>
        <h2 className="text-2xl font-bold text-[#0F172A]">Learning Videos</h2>
        <p className="text-sm text-slate-500 mt-1">Watch tutorials to get the most out of XLabelSync</p>
      </div>

      {videos.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="no-videos">
          <Video className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">No videos available yet</p>
          <p className="text-sm text-slate-400 mt-1">Check back soon for tutorials and guides</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="video-grid">
          {videos.map(video => {
            const embedUrl = getEmbedUrl(video.video_url);
            const local = isLocalVideo(video);
            return (
              <div key={video.id}
                className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                data-testid={`video-card-${video.id}`}>
                {local ? (
                  <div className="aspect-video bg-black">
                    <video
                      src={`${API}${video.video_url}`}
                      controls
                      className="w-full h-full"
                      preload="metadata"
                      data-testid={`video-player-${video.id}`}
                    />
                  </div>
                ) : embedUrl ? (
                  <div className="aspect-video">
                    <iframe
                      src={embedUrl}
                      title={video.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <a href={video.video_url} target="_blank" rel="noopener noreferrer"
                    className="block aspect-video bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center group">
                    <div className="w-16 h-16 rounded-full bg-[#0052FF]/10 group-hover:bg-[#0052FF]/20 flex items-center justify-center transition-colors">
                      <Play className="w-8 h-8 text-[#0052FF] ml-1" />
                    </div>
                  </a>
                )}
                <div className="p-5">
                  <h3 className="font-semibold text-[#0F172A] mb-1">{video.title}</h3>
                  {video.description && (
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3">{video.description}</p>
                  )}
                  {local ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
                      <Video className="w-3.5 h-3.5" /> Local Video
                    </span>
                  ) : (
                    <a href={video.video_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0052FF] hover:text-[#0040CC]"
                      data-testid={`watch-video-${video.id}`}>
                      <ExternalLink className="w-3.5 h-3.5" /> Watch Video
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
